import { OpenInNewOutlined } from '@mui/icons-material'
import { Link, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import EllipsisTypography from '@/components/dataDisplay/EllipsisTypography'
import type { ResourceSetMember } from '@/features/prequalification/localControl/types'
import { ResourceRouteInformation } from '@/features/resource/constants'

type ResourceLinkProps = {
  resource: ResourceSetMember
}

/**
 * A resource set resource's identity: its name linking to the resource details page
 * (opened in a new tab so the resource set context is preserved), with the resource id
 * beneath as a muted monospace caption.
 *
 * When a name can't be resolved the API returns the id *as* the name (see
 * `resourceSearch.toResourceSetMember` and the backend's own fallback). Rather than
 * render a bare UUID as if it were a name, we surface an explicit "Unnamed
 * resource" label — the id is still shown (and linked) below.
 */
function ResourceLink({ resource }: Readonly<ResourceLinkProps>) {
  const { t } = useTranslation()
  const hasName = resource.name !== resource.resourceId
  const label = hasName ? resource.name : t('prequalifications.resource_set.resources.unnamed')

  return (
    <Stack sx={{ minWidth: 0, gap: 0.25 }}>
      <Link
        aria-label={t('prequalifications.resource_set.resources.open_resource', { resourceName: label })}
        component={RouterLink}
        rel="noopener noreferrer"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          width: 'fit-content',
          maxWidth: '100%',
          color: hasName ? 'primary.main' : 'text.secondary',
        }}
        target="_blank"
        to={ResourceRouteInformation.RESOURCE_DETAILS.navigationPath(resource.resourceId)}
        underline="hover"
      >
        <EllipsisTypography
          component="span"
          sx={{ color: 'inherit', fontStyle: hasName ? 'normal' : 'italic' }}
          tooltipTitle={hasName ? resource.name : undefined}
          variant="body2"
        >
          {label}
        </EllipsisTypography>
        <OpenInNewOutlined sx={{ fontSize: 14, flexShrink: 0 }} />
      </Link>
      <Typography color="text.secondary" sx={{ fontFamily: 'monospace' }} variant="caption">
        {resource.resourceId}
      </Typography>
    </Stack>
  )
}

export default ResourceLink
